import { Fragment } from 'react'
import type { Series, Unit } from '@pharmacia/core'
import { everyNth, exact, niceScale, tick } from './format.ts'
import { bar, box, Tooltip, useHover } from './plot-core.tsx'
import { colorFor, SOLO } from './seriesColor.ts'

const GAP = 2        // surface showing between adjacent bars and stacked bands
const RADIUS = 4     // the rounded data end

/**
 * Magnitude across a time axis, stacked when the series are parts of one
 * whole (CGST and SGST of the same invoice) rather than separate measures.
 */
export function BarPlot({ id, categories, series, unit, width, height = 220, stacked = false }: {
  id: string
  categories: string[]
  series: Series[]
  unit: Unit
  width: number
  height?: number
  stacked?: boolean
}) {
  const n = categories.length
  const columnTotal = (i: number) =>
    series.reduce((s, ser) => s + (ser.data[i] ?? 0), 0)
  const peak = stacked
    ? Math.max(...categories.map((_, i) => columnTotal(i)), 0)
    : Math.max(...series.flatMap(s => s.data).map(v => v ?? 0), 0)

  const { max, ticks } = niceScale(peak)
  const labels = ticks.map(t => tick(t, unit))
  const gutter = Math.max(34, Math.max(...labels.map(l => l.length)) * 6.4 + 10)

  const b = box(width, height, gutter, 26)
  const slot = n ? b.iw / n : b.iw
  // A column never grows past 44px: a chart of three days should not be
  // three slabs, and the eye reads the heights, not the areas.
  const colW = Math.max(2, Math.min(slot - Math.max(GAP, slot * 0.22), 44))
  const y = (v: number) => b.top + b.ih - (v / max) * b.ih
  const cx = (i: number) => b.left + slot * i + slot / 2

  const { tip, at, show, clear } = useHover()
  const step = everyNth(n, Math.max(2, Math.floor(b.iw / 50)))
  const color = (s: Series, i: number) => (series.length === 1 ? SOLO : colorFor(id, s.key ?? String(i)))

  return (
    <>
      <svg
        className="viz-svg" width={width} height={height}
        role="img" aria-label={series.map(s => s.label).join(', ')}
        onMouseLeave={clear}
      >
        {ticks.map(t => (
          <Fragment key={t}>
            <line className="viz-gridline" x1={b.left} x2={b.left + b.iw} y1={y(t)} y2={y(t)} />
            <text className="viz-tick" x={b.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
              {tick(t, unit)}
            </text>
          </Fragment>
        ))}

        {categories.map((c, i) => {
          const sw = stacked ? colW : (colW - GAP * (series.length - 1)) / series.length
          let floor = b.top + b.ih   // stacked bands pile up from the baseline

          return (
            <g key={i} opacity={at === null || at === i ? 1 : 0.55}>
              {series.map((s, si) => {
                const v = s.data[i]
                if (v === null || v === undefined || v <= 0) return null
                const full = (v / max) * b.ih

                if (stacked) {
                  // The 2px gap is taken out of the band, not added to the
                  // column, so the stack still totals what the axis says.
                  const h = Math.max(0, full - (si > 0 ? GAP : 0))
                  const top = floor - full
                  floor = top
                  return (
                    <path
                      key={s.key} className="viz-bar" fill={color(s, si)}
                      d={bar(cx(i) - colW / 2, top + (si > 0 ? GAP : 0), colW, h, RADIUS, 'top')}
                    />
                  )
                }
                return (
                  <path
                    key={s.key} className="viz-bar" fill={color(s, si)}
                    d={bar(
                      cx(i) - colW / 2 + si * (sw + GAP), y(v), sw,
                      b.top + b.ih - y(v), RADIUS, 'top',
                    )}
                  />
                )
              })}
            </g>
          )
        })}

        <line className="viz-baseline" x1={b.left} x2={b.left + b.iw} y1={y(0)} y2={y(0)} />

        {categories.map((c, i) => (i % step === 0 ? (
          <text key={`c${i}`} className="viz-cat" x={cx(i)} y={height - 8} textAnchor="middle">{c}</text>
        ) : null))}

        {categories.map((c, i) => (
          <rect
            key={`hit${i}`} className="viz-hit"
            x={b.left + slot * i} y={b.top} width={slot} height={b.ih}
            onMouseMove={e => show(i, {
              title: c,
              rows: [
                ...series.map((s, si) => ({
                  color: color(s, si), label: s.label, value: exact(s.data[i] ?? null, s.unit ?? unit),
                })),
                ...(stacked && series.length > 1
                  ? [{ color: 'transparent', label: 'Total', value: exact(columnTotal(i), unit) }]
                  : []),
              ],
            }, e)}
          />
        ))}
      </svg>
      <Tooltip tip={tip} />
    </>
  )
}
